/* eslint-disable react/react-in-jsx-scope */
import { Badge, Button, Tooltip } from "@material-ui/core";
import MuiTimelineConnector from "@material-ui/lab/TimelineConnector";
import MuiTimelineContent from "@material-ui/lab/TimelineContent";
import MuiTimelineDot from "@material-ui/lab/TimelineDot";
import MuiTimelineItem from "@material-ui/lab/TimelineItem";
import TimelineSeparator from "@material-ui/lab/TimelineSeparator";
import clsx from "clsx";
import { theme } from "config/theme";
import { COLORS } from "constants/colors";
import useText from "constants/text";
import { ParcelsCount, ParcelsCountText } from "domains/Dashboard/components/Styles";
import { EActionType, Items, Mission, Stop, Stop as StopType, TaskMission } from "generated/graphql";
import { useMemo, useState } from "react";
import styled from "styled-components";
import { vehicleToReturn } from "domains/Request/component/Tour/refontTourRequest/Collapse";
import { UserIcon } from "icons";
import DetailsParcelMsg from "./details/DetailsParcels";
import { AddressPickup } from "./details/styles";
import { useTaskStyles } from "./style";

export const TimelineItem = styled(MuiTimelineItem)`
  min-height: 30px;
  margin-left: 10px;
  justify-content: space-between;
  &.dot {
    min-height: 20px;
  }
  :before {
    content: none;
  }
  ${theme.breakpoints.down("xs")} {
    width: fit-content;
  }
`;

export const TimelineContent = styled(MuiTimelineContent)`
  color: #707070;
  font-size: 11px;
  font-weight: 900;
  height: fit-content;
  margin-left: 22px;
  padding: 0 5px;
  position: absolute;
  top: 2px;
`;

export const TimelineConnector = styled(MuiTimelineConnector)`
  width: 1px;
  &.dot {
    margin-left: 7px;
  }
`;

export const TimelineDot = styled(MuiTimelineDot)<{
  customColor: string;
  backgroundColor?: string;
  textColor?: string;
}>`
  align-items: center;
  background-color: transparent;
  box-shadow: none;
  color: ${(props) => props?.backgroundColor || COLORS.primary} !important;
  display: flex;
  font-size: 10px;
  font-weight: bold;
  justify-content: center;
  height: 18px;
  margin: 0;
  width: 18px;
  border-color: ${(props) => props?.customColor || COLORS.primary} !important;

  &.pickup.outlined {
    color: ${(props) => props?.backgroundColor || COLORS.primary} !important;
    border-color: ${(props) => props?.customColor || COLORS.primary} !important;
  }

  &.pickup.contained {
    background-color: ${(props) => props?.backgroundColor || COLORS.primary} !important;
    color: ${(props) => props?.textColor || COLORS.primary} !important;
  }

  &.delivery.outlined {
    color: #ebae23;
    border-color: #ebae23;
  }

  &.delivery.contained {
    background-color: #ebae23;
  }
  &.done {
    color: #c9c9c9;
    border-color: #c9c9c9;
  }

  &.dot {
    height: 5px;
    margin-left: 7px;
    padding: 0;
    width: 4.5px;
  }
`;

export const StopLabel = {
  [EActionType.Pickup]: "P",
  [EActionType.Delivery]: "D",
  [EActionType.PickupDelivery]: "P/D",
  [EActionType.Unknown]: "S",
};

interface StopProps {
  stop?: any;
  showItems?: boolean;
  onChangeTodo?: (value: string, stop: StopType) => void;
  primaryColor?: string;
  editMode?: boolean;
  task?: TaskMission;
  templateStops?: StopType[];
  shouldShowActions?: boolean;
  isTaskDone?: boolean;
  getNumberOfProductsToPick?: () => number;
  count?: number;
  isLastAxe?: boolean;
  parcelIds?: string;
  mission?: Mission;
}

const TaskDetails = ({ primaryColor, task, isLastAxe = false, mission }: StopProps) => {
  const styles = useTaskStyles({});
  const { text } = useText();

  const getStopItemsCount = (items: Items[]) => items.reduce((counter, item) => counter + item.quantity, 0);
  const isLast = (index: number): boolean => {
    return isLastAxe && index === task?.stops.length - 1;
  };
  const [open, setOpen] = useState(false);
  const [stop, setStop] = useState<Stop>();

  const openParcel = (stop: Stop) => {
    setOpen(true);
    setStop(stop);
  };
  const closeParcelDetails = () => {
    setOpen(false);
  };

  const renderStop = (task: TaskMission) => {
    const sortedStops = task?.stops?.sort((a, b) => (a.action === EActionType.Pickup ? -1 : 1));
    return (
      <div>
        {sortedStops?.map(
          (stop: Stop, index: number) =>
            stop && (
              <div key={index}>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      backgroundColor={["P", "P/D"]?.includes(StopLabel[stop?.action]) ? "#259940" : "#fff"}
                      customColor="#259940"
                      textColor={["D"]?.includes(StopLabel[stop?.action]) ? "#259940" : "#fff"}
                      className={clsx("pickup", {
                        outlined: StopLabel[stop?.action] === "P",
                        contained:
                          StopLabel[stop?.action] === "P" ||
                          StopLabel[stop?.action] === "D" ||
                          StopLabel[stop?.action] === "P/D" ||
                          StopLabel[stop?.action] === StopLabel.UNKNOWN ||
                          !StopLabel[stop?.action],
                      })}
                    >
                      {stop?.action ? StopLabel[stop?.action] : StopLabel.UNKNOWN}
                    </TimelineDot>
                    {!isLast(index) && <TimelineConnector />}
                  </TimelineSeparator>

                  <TimelineContent>
                    <Tooltip title={stop?.address?.address}>
                      <AddressPickup>{`${stop?.address?.address.substring(0, 25)}...`}</AddressPickup>
                    </Tooltip>
                  </TimelineContent>
                  <Button onClick={() => openParcel(stop)} className={styles.itemButton}>
                    <ParcelsCount
                      backgroundColor={stop?.items?.length ? "#f2994a" : "#bdbdbd"}
                      borderRadius="100px"
                      color="#fff"
                      padding="10px"
                      marginTop="0px"
                      height="31px"
                      width="100px"
                    >
                      <ParcelsCountText color="#fff">
                        {text.requestQuote.product}
                        <Badge
                          classes={{ badge: styles.customBadgeTR }}
                          badgeContent={getStopItemsCount(stop?.items || [])}
                          color="secondary"
                        ></Badge>
                      </ParcelsCountText>
                    </ParcelsCount>
                  </Button>
                </TimelineItem>
                {!isLast(index) && (
                  <TimelineItem className="dot">
                    <TimelineSeparator>
                      <TimelineDot
                        customColor={stop?.isDisabled ? COLORS.gray : primaryColor}
                        className={clsx("dot", "outlined", "pickup")}
                      />
                      {!isLast(index) && <TimelineConnector className="dot" />}
                    </TimelineSeparator>
                    <TimelineContent />
                  </TimelineItem>
                )}
              </div>
            )
        )}
      </div>
    );
  };
  return (
    <>
      {mission?.tasks?.map((task) => {
        return (
          <div key={task?.uid} className={styles.axe}>
            <div className={styles.axeHeading}>
              <p className={styles.axeText}>{task?.stops?.[0]?.axe?.name || "N/A"}</p>
              <div className={styles.axeLine}></div>
              <div className={styles.taskUid}>{task?.uid}</div>
            </div>
            <div className={styles.infosContainer}>
              <div className={styles.driver}>
                <UserIcon width={21} height={21} />
                {`${task?.task_driver?.driver_firstname || ""} ${task?.task_driver?.driver_lastname || "N/A"}`}
              </div>
              {task?.vehiculeType ? (
                <div className={styles.vehicules}>
                  <span>{vehicleToReturn[task?.vehiculeType]}</span>
                  <span className="vehicule-name">{task?.vehiculeType?.replace(/_/g, " ") || "N/A"}</span>
                </div>
              ) : null}
            </div>
            {renderStop(task)}
          </div>
        );
      })}
      <DetailsParcelMsg
        openDialogDetails={open}
        onCloseDialogDetails={closeParcelDetails}
        stop={stop || {}}
        items={stop?.items || []}
      />
    </>
  );
};

export default TaskDetails;
